export type BillingLogEntryResponse = {
	id: number
	contractAfe: string | null
	purchaseOrder: string
	costCode: string | null
	workOrder: string | null
	line: string | null
	projectId: number
}

export type BillingLogEntryUpsertRequest = {
	contractAfe: string | null
	purchaseOrder: string
	costCode: string | null
	workOrder: string | null
	line: string | null
}

export const formatBillingLogEntryOptionLabel = (entry: BillingLogEntryResponse): string =>
	[
		entry.purchaseOrder && `PO ${entry.purchaseOrder}`,
		entry.workOrder && `WO ${entry.workOrder}`,
		entry.line && `Line ${entry.line}`,
		entry.contractAfe && `Contract/AFE ${entry.contractAfe}`,
		entry.costCode && `Cost Code ${entry.costCode}`,
	]
		.filter(Boolean)
		.join(' · ') || `Billing entry ${entry.id}`

export const compareBillingLogEntriesForDisplay = (
	a: BillingLogEntryResponse,
	b: BillingLogEntryResponse,
): number =>
	a.purchaseOrder.localeCompare(b.purchaseOrder) ||
	(a.workOrder ?? '').localeCompare(b.workOrder ?? '') ||
	(a.line ?? '').localeCompare(b.line ?? '')

const distinctSorted = (values: (string | null)[]): string[] =>
	Array.from(new Set(values.map((v) => v?.trim() ?? '').filter(Boolean))).sort()

const entriesForPurchaseOrder = (logs: BillingLogEntryResponse[], purchaseOrder: string) =>
	logs.filter((log) => log.purchaseOrder.trim() === purchaseOrder.trim())

export const getPurchaseOrderOptions = (logs: BillingLogEntryResponse[]): string[] =>
	distinctSorted(logs.map((log) => log.purchaseOrder))

export const getWorkOrderOptions = (
	logs: BillingLogEntryResponse[],
	purchaseOrder: string,
): string[] =>
	distinctSorted(entriesForPurchaseOrder(logs, purchaseOrder).map((log) => log.workOrder))

/**
 * A work order is needed when every entry of the purchase order has one. A purchase order with no
 * work orders, or with any entry logged without one, can be used without a work order.
 */
export const isWorkOrderRequired = (
	logs: BillingLogEntryResponse[],
	purchaseOrder: string,
): boolean => {
	const entries = entriesForPurchaseOrder(logs, purchaseOrder)
	return entries.length > 0 && entries.every((log) => !!log.workOrder?.trim())
}

/** The work order to fill in when a purchase order is picked: its only one, when one is required. */
export const getDefaultWorkOrder = (
	logs: BillingLogEntryResponse[],
	purchaseOrder: string,
): string => {
	const options = getWorkOrderOptions(logs, purchaseOrder)
	return options.length === 1 && isWorkOrderRequired(logs, purchaseOrder) ? options[0] : ''
}

export type BillingOrderValues = { purchaseOrder?: string; workOrder?: string }

/**
 * Whether a form must have a work order. A purchase order and work order left as they were saved
 * are kept even if the log has changed since, as the API does.
 */
export const formNeedsWorkOrder = (
	logs: BillingLogEntryResponse[],
	values: BillingOrderValues,
	saved?: BillingOrderValues,
): boolean => {
	const purchaseOrder = values.purchaseOrder?.trim() ?? ''
	const keepsSaved =
		!!saved &&
		purchaseOrder === (saved.purchaseOrder?.trim() ?? '') &&
		(values.workOrder?.trim() ?? '') === (saved.workOrder?.trim() ?? '')
	return !keepsSaved && isWorkOrderRequired(logs, purchaseOrder)
}
